/**
 * Millimetres → screen coordinates for the schema drawing.
 *
 * Presentation only. The node coordinates, the closure sums and every length
 * come from the backend; this module scales them into a viewport and works out
 * where a dimension line goes. It never derives a measurement.
 *
 * The y axis keeps the backend's orientation: a growing yMm runs downwards on
 * screen, so the drawing matches the schema coordinates one to one.
 */

export interface MmPoint {
  id: string;
  xMm: number;
  yMm: number;
}

export interface ScreenPoint {
  x: number;
  y: number;
}

export interface Projection {
  /** Screen position per node id. */
  points: Record<string, ScreenPoint>;
  /** Screen pixels per millimetre — the one actually drawn at. */
  scale: number;
  /**
   * The scale a plain fit would have picked, whatever `scale` says.
   *
   * The lower bound for zooming: below it the shape would be smaller than the
   * board, which is not a view anybody asked for. Reported rather than
   * recomputed by the caller, because the caller would have to pass the same
   * node list a second time to get it.
   */
  fitScale: number;
  width: number;
  height: number;
  /** Centre of the drawn shape — dimension lines are pushed away from it. */
  centroid: ScreenPoint;
  /**
   * The millimetre point currently sitting in the middle of the viewport —
   * the shape's own centre by default, or `options.centreMm` where the
   * caller named one.
   *
   * Not {@link centroid}: that one is the shape's own centroid, fixed to the
   * shape and given in screen pixels, used to push dimension lines outward.
   * This one is the viewport's centre, given in millimetres, and moves with
   * `options.centreMm`. A caller hands it back into `zoomBy` as the previous
   * centre to hold on to when `view.centreMm` is itself `null` — coming out
   * of the fit view there is no zoomed centre yet, and this is what stands
   * in for it (Befund 1, walk-through 10.09.2026; see `zoomBy` in
   * `viewport.ts` for why guessing the anchor instead was wrong).
   */
  centreMm: { xMm: number; yMm: number };
}

export interface ProjectionOptions {
  width: number;
  height: number;
  /** Room for dimension lines and labels around the shape. */
  padding: number;
  /**
   * Pixels per millimetre, where the caller has one of its own.
   *
   * Omitted, the shape is fitted as it always was. This is the override that
   * was removed on 10.08.2026 for want of a caller — the zoom on `/planung`
   * is that caller (design 2026-09-10, D-4). The length stays true to scale
   * either way: this changes how large the drawing is, never its proportion
   * (E-13).
   */
  scale?: number;
  /**
   * The millimetre point to put in the middle of the viewport.
   *
   * Only meaningful together with `scale`; without it the shape is centred as
   * before. Its own field rather than a pixel offset so that a caller can
   * hold on to „this corner of the house", which survives a change of scale,
   * instead of to a pixel, which does not.
   */
  centreMm?: { xMm: number; yMm: number };
}

/**
 * The scale a plain fit picks for these nodes — the lower bound for a zoom.
 *
 * Exported because two callers need the same number without projecting twice:
 * `projectRun` uses it as its default, and the board clamps its zoom against
 * it.
 */
export function fitScale(nodes: MmPoint[], options: ProjectionOptions): number {
  if (nodes.length === 0) return 1;
  const spans = spansOf(nodes);
  const usableWidth = Math.max(options.width - 2 * options.padding, 1);
  const usableHeight = Math.max(options.height - 2 * options.padding, 1);
  return Math.min(usableWidth / spans.spanX, usableHeight / spans.spanY);
}

/**
 * The largest scale anybody may zoom to: one pixel per millimetre.
 *
 * At that scale a centimetre is ten pixels, which is exactly the grid a
 * placement snaps to (`GRID_MM`). Finer than the grid there is nothing to
 * place, so the cap costs nothing and keeps the projection out of numbers
 * where a 17-metre facade would be 17.000 px wide.
 */
export const MAX_SCALE = 1;

/**
 * A wanted scale, brought inside the two bounds.
 *
 * The lower bound is the fit and not a constant: „alles zeigen" is the
 * smallest useful view of *this* shape, and a small outline fits at a scale
 * above {@link MAX_SCALE} — clamping that one down would make „alles zeigen"
 * unreachable. Hence `Math.max(fitted, …)` on the outside.
 */
export function clampScale(wanted: number, fitted: number): number {
  return Math.max(fitted, Math.min(wanted, Math.max(MAX_SCALE, fitted)));
}

/** Bounding box of the nodes, with each span at least one millimetre. */
function spansOf(nodes: MmPoint[]): {
  minX: number;
  minY: number;
  spanX: number;
  spanY: number;
} {
  const xs = nodes.map((node) => node.xMm);
  const ys = nodes.map((node) => node.yMm);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    minX,
    minY,
    spanX: Math.max(Math.max(...xs) - minX, 1),
    spanY: Math.max(Math.max(...ys) - minY, 1),
  };
}

/**
 * Fits the run into the viewport at a single uniform scale, so the drawing
 * stays true to proportion — a section read as ten times too long has to look
 * ten times too long (E-13).
 *
 * Fits the nodes handed in by default. A caller with a scale of its own — the
 * zoom on `/planung` — can override it through `options.scale` and move the
 * viewport's centre through `options.centreMm`; `fitScale` on the result
 * reports what the default would have picked either way.
 */
export function projectRun(nodes: MmPoint[], options: ProjectionOptions): Projection {
  const { width, height } = options;

  if (nodes.length === 0) {
    return {
      points: {},
      scale: 1,
      fitScale: 1,
      width,
      height,
      centroid: { x: width / 2, y: height / 2 },
      // No nodes, no shape to centre on — the same „nothing to invert
      // against" case `PlanningBoard.pointerMm` already falls back to.
      centreMm: { xMm: 0, yMm: 0 },
    };
  }

  const fitted = fitScale(nodes, options);
  const scale = options.scale ?? fitted;
  const { minX, minY, spanX, spanY } = spansOf(nodes);

  // Where the middle of the viewport sits, in millimetres: the middle of the
  // shape unless the caller named a point of its own.
  const centreXMm = options.centreMm?.xMm ?? minX + spanX / 2;
  const centreYMm = options.centreMm?.yMm ?? minY + spanY / 2;

  // One offset for every point: the pixel that the millimetre origin lands on.
  const offsetX = width / 2 - centreXMm * scale;
  const offsetY = height / 2 - centreYMm * scale;

  const points: Record<string, ScreenPoint> = {};
  for (const node of nodes) {
    points[node.id] = {
      x: offsetX + node.xMm * scale,
      y: offsetY + node.yMm * scale,
    };
  }

  return {
    points,
    scale,
    fitScale: fitted,
    width,
    height,
    // The centroid of the **shape**, never of the viewport. `dimensionLine`
    // pushes a measurement away from it; a viewport centroid would flip every
    // label to the wrong side of its wall the moment somebody panned
    // (design §7, review finding I-5).
    centroid: {
      x: offsetX + (minX + spanX / 2) * scale,
      y: offsetY + (minY + spanY / 2) * scale,
    },
    // The **viewport's** centre, in millimetres — `centreXMm`/`centreYMm`
    // themselves, reported rather than left for a caller to recompute (and
    // the recomputation `zoomBy` used to do instead of reading this back is
    // Befund 1).
    centreMm: { xMm: centreXMm, yMm: centreYMm },
  };
}

export interface DimensionLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Anchor for the measurement label, already pushed clear of the line. */
  labelX: number;
  labelY: number;
  /** Degrees; keeps the label upright along a vertical section, and along a
   * slant turns it by the slant's own angle in (−90°, 90°]. */
  rotation: number;
}

/**
 * A dimension line parallel to its section, offset to the outside of the shape —
 * the same convention as a workshop drawing, so K60 reads it without a legend.
 */
export function dimensionLine(
  from: ScreenPoint,
  to: ScreenPoint,
  centroid: ScreenPoint,
  offset: number,
): DimensionLine {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;

  // Unit normal, flipped to point away from the middle of the shape.
  let nx = -dy / length;
  let ny = dx / length;
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  if ((midX - centroid.x) * nx + (midY - centroid.y) * ny < 0) {
    nx = -nx;
    ny = -ny;
  }

  const x1 = from.x + nx * offset;
  const y1 = from.y + ny * offset;
  const x2 = to.x + nx * offset;
  const y2 = to.y + ny * offset;

  // A chamfer's slant (design 2026-10-18 §6.5) is the one section that runs
  // along neither axis. Its figure turns with it — by the section's own
  // angle, folded into (−90°, 90°] so it never reads upside down — and is
  // pushed off it along the normal. Every axis-parallel section keeps exactly
  // what it had: 0° or −90°, and the old push.
  if (Math.abs(dx) > DIAGONAL_EPSILON * length && Math.abs(dy) > DIAGONAL_EPSILON * length) {
    let rotation = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (rotation > 90) rotation -= 180;
    if (rotation <= -90) rotation += 180;
    return {
      x1,
      y1,
      x2,
      y2,
      labelX: (x1 + x2) / 2 + nx * DIAGONAL_LABEL_PUSH,
      labelY: (y1 + y2) / 2 + ny * DIAGONAL_LABEL_PUSH,
      rotation,
    };
  }

  const isVertical = Math.abs(dy) > Math.abs(dx);

  return {
    x1,
    y1,
    x2,
    y2,
    labelX: (x1 + x2) / 2 + (isVertical ? nx * 10 : 0),
    labelY: (y1 + y2) / 2 + (isVertical ? 0 : ny * 8),
    rotation: isVertical ? -90 : 0,
  };
}

/**
 * Below this share of its length, a section's run along the other axis is
 * rounding, not a slant — the projection of an axis-parallel wall is exact,
 * but a caller may hand in points that went through arithmetic of its own.
 */
const DIAGONAL_EPSILON = 1e-6;
/** How far a slant's figure stands off its dimension line — between the
 * horizontal section's 8 and the vertical one's 10. */
const DIAGONAL_LABEL_PUSH = 9;

export interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Cross bars along a section that carries grating. Drawn between the two rails,
 * these read as a grating rather than as a highlighted line — the drawing
 * borrows the vocabulary of the product it plans.
 */
export function gratingHatch(
  from: ScreenPoint,
  to: ScreenPoint,
  spacing: number,
  halfWidth: number,
): Segment[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length < spacing || spacing <= 0) return [];

  const ux = dx / length;
  const uy = dy / length;
  const nx = -uy * halfWidth;
  const ny = ux * halfWidth;

  const segments: Segment[] = [];
  for (let distance = spacing / 2; distance < length; distance += spacing) {
    const px = from.x + ux * distance;
    const py = from.y + uy * distance;
    segments.push({ x1: px - nx, y1: py - ny, x2: px + nx, y2: py + ny });
  }
  return segments;
}

/** The two rails of a grating section, offset either side of the run. */
export function railPair(from: ScreenPoint, to: ScreenPoint, halfWidth: number): [Segment, Segment] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  const nx = (-dy / length) * halfWidth;
  const ny = (dx / length) * halfWidth;

  return [
    { x1: from.x + nx, y1: from.y + ny, x2: to.x + nx, y2: to.y + ny },
    { x1: from.x - nx, y1: from.y - ny, x2: to.x - nx, y2: to.y - ny },
  ];
}

/** Round steps a scale bar may use, from a centimetre up.
 *
 * The three small steps are for the zoom: at one pixel per millimetre the
 * old smallest step of 500 mm drew a bar 500 px long — half the board, and
 * mute about the scale it claimed to state (design §7.1). */
const BAR_STEPS_MM = [10, 50, 100, 500, 1000, 2000, 5000, 10_000, 20_000, 50_000];

/** Largest round length that still fits into `maxPixels` at this scale. */
export function scaleBarMm(scale: number, maxPixels = 120): number {
  let chosen = BAR_STEPS_MM[0] as number;
  for (const step of BAR_STEPS_MM) {
    if (step * scale <= maxPixels) chosen = step;
  }
  return chosen;
}
